import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Head from 'next/head';
import getConfig from 'next/config';
import cx from 'classnames';

import { BodyScrollLockProvider } from 'contexts/BodyScrollLockContext';

import useGlobalElementRef from 'hooks/useGlobalElementRef';

import Footer from './components/Footer';
import Header from './components/Header';
import SvgIcons from './components/SvgIcons';

import styles from './styles.module.scss';

const {
	publicRuntimeConfig: { baseUrl, basePath, services }
} = getConfig();

const defaultImageParams = {
	type: 'website',
	image: `${baseUrl}/images/logo-og-v2.png`,
	imageWidth: 1600,
	imageHeight: 800
};

const pageLoadTimeout = 2000;

const Layout = ({ children, noIndex, anchors }) => {
	const [isPageLoading, setPageLoading] = useState(true);
	const { asPath } = useRouter();
	const layoutRef = useGlobalElementRef('layout');

	const url = `${baseUrl}${asPath}`;

	useEffect(() => {
		window.addEventListener('load', () => setPageLoading(false));
		setTimeout(() => setPageLoading(false), pageLoadTimeout);

		/* eslint-disable */
		window.ym =
			window.ym ||
			function () {
				(window.ym.a = window.ym.a || []).push(arguments);
			};
		window.ym.l = 1 * new Date();
		/* eslint-enable */

		window.ym(services.yandex.analyticsId, 'init', {
			clickmap: true,
			trackLinks: true,
			accurateTrackBounce: true,
			webvisor: true,
			ecommerce: 'dataLayer'
		});
	}, []);

	useEffect(() => {
		if (!isPageLoading) {
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://mc.yandex.ru/metrika/tag.js';

			const head = document.head || document.getElementsByTagName('head')[0];
			head.appendChild(script);
		}
	}, [isPageLoading]);

	const currentYear = new Date().getFullYear();

	return (
		<>
			<Head>
				<title>Спецпроект Выставки музеев — {currentYear}</title>

				<meta
					name="description"
					content="Афиша значимых выставок России. Планируем культурные выходные на весь год"
				/>

				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta httpEquiv="X-UA-Compatible" content="IE=edge" />
				<meta name="format-detection" content="telephone=no" />

				<meta property="og:type" content="website" />
				<meta property="og:url" content={url} />
				<meta
					name="og:title"
					content={`Спецпроект Выставки музеев — ${currentYear}`}
				/>
				<meta
					property="og:description"
					content="Афиша значимых выставок России. Планируем культурные выходные на весь год"
				/>

				{noIndex && <meta name="robots" content="noindex, nofollow" />}

				<meta property="og:image" content={defaultImageParams.image} />
				<meta
					property="og:image:width"
					content={defaultImageParams.imageWidth}
				/>
				<meta
					property="og:image:height"
					content={defaultImageParams.imageHeight}
				/>

				<link
					rel="preload"
					as="font"
					href={`${basePath}/fonts/Roboto/Roboto-Regular.woff`}
					type="font/woff"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					as="font"
					href={`${basePath}/fonts/Roboto/Roboto-Medium.woff`}
					type="font/woff"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					as="font"
					href={`${basePath}/fonts/Roboto/Roboto-Bold.woff`}
					type="font/woff"
					crossOrigin="anonymous"
				/>
				<link
					rel="icon"
					href={`${basePath}/favicons/favicon.ico`}
					sizes="any"
				/>
				<link
					rel="icon"
					href={`${basePath}/favicons/favicon.svg`}
					type="image/svg+xml"
				/>
				<link
					rel="apple-touch-icon"
					href={`${basePath}/favicons/apple-touch-icon.png`}
				/>
				<link rel="manifest" href={`${basePath}/favicons/site.webmanifest`} />
			</Head>

			<BodyScrollLockProvider>
				<div ref={layoutRef} className={styles.Layout}>
					<Header {...{ anchors }} />
					<main className={styles.Layout_Main}>{children}</main>
					<Footer />
				</div>
			</BodyScrollLockProvider>

			<SvgIcons />
		</>
	);
};

Layout.propTypes = {
	children: PropTypes.node.isRequired,
	noIndex: PropTypes.bool,
	anchors: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			url: PropTypes.string
		})
	).isRequired
};

const LayoutBlock = forwardRef(({ children, className, anchorId }, ref) => (
	<div ref={ref} id={anchorId} className={cx(styles.block, className)}>
		{children}
	</div>
));

LayoutBlock.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	anchorId: PropTypes.string
};

export { Layout, LayoutBlock };
